import Button from "../../button/Button";
import styles from "./CancelSubscriptionModal.module.scss";
import close from "../../../assets/icons/close.svg";
import done from "../../../assets/icons/done.svg";
import redClose from "../../../assets/icons/close-red.svg";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { subscriptionData } from "../../../types/details-api-types";
import { EVENT_NAMES } from "../../../types/event-names";
import { trackEvent } from "../../../utils/analytics";

interface CancelSubscriptionModalProps {
  setIsModalPauseSubVisible: (arg: boolean) => void;
  setIsCancelSubVisible: (arg: boolean) => void;

  subscriptionItem: subscriptionData;
  callbackAction: (action: "deactivate" | "close" | "offer", data?: subscriptionData | null) => void;
}

const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  setIsModalPauseSubVisible,
  setIsCancelSubVisible,
  callbackAction,
  subscriptionItem,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  const isWithin40Days = (dateStr: string) => {
    const today = new Date().getTime();
    const targetDate = new Date(dateStr).getTime();

    // Calculate difference in days
    const diffDays = (targetDate - today) / (1000 * 60 * 60 * 24);

    return diffDays <= 40 && diffDays >= 0;
  };

  const totalCharges =
    subscriptionItem?.invoices?.reduce((total, invoice) => {
      if (invoice?.type?.toLowerCase() === "charge") {
        return total + (Number(invoice.priceTotal) || 0);
      }
      return total;
    }, 0) || 0;

  const checkRecurringDiffersFromCharge = (subscriptionItem: { invoices: any[] }, totalCharges: any) => {
    const recurringTotal =
      subscriptionItem?.invoices?.reduce((total, invoice) => {
        if (invoice?.type?.toLowerCase() === "recurring") {
          return total + (Number(invoice.priceTotal) || 0);
        }
        return total;
      }, 0) || 0;

    // Return true if no recurring invoice exists or totals differ
    return recurringTotal === 0 || recurringTotal !== totalCharges;
  };

  const recurringCount =
    subscriptionItem?.invoices?.filter((invoice) => invoice?.type?.toLowerCase() === "recurring").length || 0;

  const isChargeInvoiceEqualToRenewal = subscriptionItem.invoices?.some(
    (invoice) =>
      invoice.type === "CHARGE" &&
      Number((invoice.priceTotal + invoice.vat).toFixed(2)) === Number(subscriptionItem.renewalPrice)
  );

  const showProcessingModal = async () => {
    setIsCancelSubVisible(false);
    setIsLoading(true);

    const shouldOffer =
      !isChargeInvoiceEqualToRenewal &&
      checkRecurringDiffersFromCharge(subscriptionItem, totalCharges) &&
      (isWithin40Days(subscriptionItem.next_payment) || recurringCount);

    const action = shouldOffer ? "offer" : "deactivate";
    callbackAction(action, subscriptionItem);

    setIsLoading(false);
  };

  useEffect(() => {
    trackEvent(EVENT_NAMES.SUBSCRIPTION_CANCELLED_INITIATED, {
      subscriptionId: subscriptionItem?.subscription_id,
    });

    trackEvent(EVENT_NAMES.SUBSCRIPTION_CANCELLED_PAUSED_OFFER);
  }, [subscriptionItem?.subscription_id]);

  return (
    <div className={styles.root}>
      <div className={styles.root_header}>
        <p className={styles.root_header_title}>{t("Are You Sure You Want to Cancel? Consider Pausing Instead!")}</p>
        <div className={styles.root_header_img} onClick={() => setIsCancelSubVisible(false)}>
          <img src={close} alt="close" />
        </div>
      </div>
      <p className={styles.root_subtitle}>{t("Choose the Best Option for Your Needs")}</p>
      <div className={styles.root_table}>
        <div className={styles.root_table_item}>
          <p className={styles.root_table_item_headerLeft}>{t("Feature")}</p>
          <p className={styles.root_table_item_content}>{t("Retain Account Setting")}</p>
          <p className={styles.root_table_item_content}>{t("Subscription Cost")}</p>
          <p className={styles.root_table_item_content}>{t("Easy Resumption")}</p>
          <p className={styles.root_table_item_contentLast}>{t("Access to Fortect Features")}</p>
        </div>
        <div className={styles.root_table_item}>
          <p className={styles.root_table_item_headerCenter}>{t("Pause Subscription")}</p>
          <div className={styles.root_table_item_content}>
            <img src={done} alt="done" />
            <p>{t("Yes")}</p>
          </div>
          <div className={styles.root_table_item_content}>
            <img src={done} alt="done" />
            <p>{t("No charges during pause")}</p>
          </div>
          <div className={styles.root_table_item_content}>
            <img src={done} alt="done" />
            <p>{t("One-click resume")}</p>
          </div>
          <div className={styles.root_table_item_content}>
            <img src={done} alt="done" />
            <p>{t("Full access until expiration")}</p>
          </div>
        </div>
        <div className={styles.root_table_item}>
          <p className={styles.root_table_item_headerRight}>{t("Cancel Subscription")}</p>
          <div className={styles.root_table_item_content}>
            <img src={redClose} alt="redClose" />
            <p>{t("No")}</p>
          </div>
          <div className={styles.root_table_item_content}>
            <img src={redClose} alt="redClose" />
            <p>{t("No charges after pause")}</p>
          </div>
          <div className={styles.root_table_item_content}>
            <img src={redClose} alt="redClose" />
            <p>{t("Re-subscribe")}</p>
          </div>
          <div className={styles.root_table_item_contentImgLast}>
            <img src={done} alt="done" />
            <p>{t("Full access until expiration")}</p>
          </div>
        </div>
      </div>
      <div className={styles.root_tableMobile}>
        <div className={styles.root_tableMobile_header}>
          <p>{t("Pause Subscription")}</p>
        </div>
        <div className={styles.root_tableMobile_item}>
          <img src={done} alt="done" />
          <p>{t("Retain Account Setting")}</p>
        </div>
        <div className={styles.root_tableMobile_item}>
          <img src={done} alt="done" />
          <p>{t("No charges during pause")}</p>
        </div>
        <div className={styles.root_tableMobile_item}>
          <img src={done} alt="done" />
          <p>{t("Easy Resumption")}</p>
        </div>
        <div className={styles.root_tableMobile_item}>
          <img src={done} alt="done" />
          <p>{t("Full access until expiration")}</p>
        </div>
      </div>
      <p className={styles.root_subtitle}>
        {t(
          "We recommend pausing your subscription if you're unsure about canceling or think you might need Fortect services in the future."
        )}
      </p>

      <div className={styles.root_footer}>
        <Button
          onClick={() => {
            setIsModalPauseSubVisible(true);
            setIsCancelSubVisible(false);
            trackEvent(EVENT_NAMES.SUBSCRIPTION_CANCELLED_PAUSED_ACCEPTED);
          }}
          text={t("Pause Subscription")}
          classNames={styles.root_footer_pause}
        />
        <Button
          onClick={() => {
            showProcessingModal();
            trackEvent(EVENT_NAMES.SUBSCRIPTION_CANCELLED_PAUSED_DECLINED);
          }}
          text={t("Cancel Subscription")}
          type="error"
          classNames={styles.root_footer_cancel}
          isLoader={isLoading}
        />
        <Button
          onClick={() => {
            showProcessingModal();
            trackEvent(EVENT_NAMES.SUBSCRIPTION_CANCELLED_PAUSED_DECLINED);
          }}
          text={t("Cancel Subscription")}
          type="error"
          classNames={styles.root_footer_cancelMobile}
          isLoader={isLoading}
        />
      </div>
    </div>
  );
};

export default CancelSubscriptionModal;
